import { Team } from '../types';

/**
 * Export team data to a JSON file for download
 */
export const exportTeam = (team: Team): void => {
  try {
    // Create export object with metadata
    const exportData = {
      version: '1.0',
      exportDate: new Date().toISOString(),
      appId: 'dream11-soccer',
      teamData: team
    };
    
    // Convert to JSON string with pretty formatting
    const jsonString = JSON.stringify(exportData, null, 2);
    
    // Create a blob from the JSON string
    const blob = new Blob([jsonString], { type: 'application/json' });
    
    // Create a URL for the blob
    const url = URL.createObjectURL(blob);
    
    // Create a temporary anchor element for downloading
    const a = document.createElement('a');
    a.href = url;
    a.download = `${team.name.replace(/\s+/g, '_')}_dream11.json`;
    
    // Trigger download
    document.body.appendChild(a);
    a.click();
    
    // Clean up
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    console.log('Team exported successfully!');
  } catch (error) {
    console.error('Failed to export team:', error);
    throw new Error('Failed to export team data');
  }
};

/**
 * Read and parse a team import file
 */
export const readTeamImportFile = (file: File): Promise<Team> => {
  return new Promise((resolve, reject) => {
    if (!file || file.type !== 'application/json') {
      reject(new Error('Invalid file format. Please select a JSON file.'));
      return;
    }
    
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const importData = JSON.parse(content);
        
        // Validate import data
        if (!importData || !importData.appId || importData.appId !== 'dream11-soccer') {
          reject(new Error('This file is not a valid Dream 11 team export.'));
          return;
        }
        
        // Check version compatibility (for future use)
        const version = importData.version || '1.0';
        if (parseFloat(version) > 1.0) {
          console.warn(`Importing from newer format version (${version}). Some features may not be supported.`);
        }
        
        // Extract and validate team data
        const team = importData.teamData;
        
        if (!team || !team.formation || !Array.isArray(team.players) || !Array.isArray(team.substitutes)) {
          reject(new Error('The import file contains invalid team data.'));
          return;
        }
        
        resolve(team as Team);
      } catch (error) {
        console.error('Failed to parse import file:', error);
        reject(new Error('Failed to parse the import file. The file may be corrupted.'));
      }
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read the import file.'));
    };
    
    reader.readAsText(file);
  });
}; 